import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { catchError, EMPTY, finalize, map, Observable, switchMap, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  BcpMessage,
  BcpCaseResponse,
  BcpCaseFilters,
  BcpCasePageResponse,
  Ebeu,
  EbeuSearchRequest,
  EbeuSearchResponse,
  Ehr,
  EhrDocument,
  EhrListFiltersValue,
  GetEhrDocumentRequest,
  GetEhrListRequest,
  GetEhrListResponse,
  EesztWarning,
  PatientSearchFormValue
} from '../types/eeszt';
import { Auth } from './auth';

@Injectable({
  providedIn: 'root'
})
export class EesztService {
  private readonly API_URL = environment.apiUrl;

  private readonly http = inject(HttpClient);
  private readonly toastr = inject(ToastrService);
  private readonly authService = inject(Auth);

  // EHR state
  ehrList = signal<Ehr[]>([]);
  ehrWarnings = signal<EesztWarning[]>([]);

  // EBEU state
  ebeuResults = signal<Ebeu[]>([]);

  // BCP state
  bcpMessages = signal<BcpMessage[]>([]);
  bcpCases = signal<BcpCaseResponse[]>([]);
  bcpPagination = signal<{ size: number; number: number; totalElements: number; totalPages: number } | null>(null);

  // Loading states
  loading = signal({
    ehrList: false,
    ehrDocument: false,
    bcpMessages: false,
    resending: false,
    ebeuSearch: false
  });

  // Selection state for BCP resend
  selectedBcpIds = signal<Set<number>>(new Set());

  loadEhrList(patient: PatientSearchFormValue, filters: EhrListFiltersValue): Observable<void> {
    const user = this.authService.currentUser();
    if (!user) {
      this.toastr.error('Felhasználó nem található');
      return EMPTY;
    }

    if (!patient.ssn || !patient.birthDate) {
      this.toastr.error('TAJ szám és születési dátum megadása kötelező');
      return EMPTY;
    }

    this.loading.update(s => ({ ...s, ehrList: true }));
    this.ehrList.set([]);
    this.ehrWarnings.set([]);

    const request: GetEhrListRequest = {
      breakGlass: filters.breakGlass ? 'true' : 'false',
      breakGlassOk: filters.breakGlassOk ?? '',
      callerTransactionId: '',
      fromDate: filters.fromDate ? this.formatDate(filters.fromDate) : '',
      ssn: patient.ssn,
      birthDate: this.formatDate(patient.birthDate),
      servicePointId: user.servicePoint?.id ?? 0,
      toDate: filters.toDate ? this.formatDate(filters.toDate) : '',
      // QUESTION: use eesztUserId instead of id ???
      userId: user.id,
      who: filters.who ?? ''
    };

    return this.http.post<GetEhrListResponse>(
      `${this.API_URL}/rest/eeszt/ehr/get-ehr-list`,
      request
    ).pipe(
      tap(response => {
        this.ehrList.set(response.data ?? []);
        this.ehrWarnings.set(response.warnings ?? []);

        if ((response.data?.length ?? 0) === 0) {
          this.toastr.info('Nincs találat a keresési feltételeknek megfelelően');
        }
      }),
      map(() => void 0),
      catchError(error => {
        console.error('EHR list error:', error);
        this.toastr.error('Hiba történt a dokumentumok lekérdezésekor');
        return EMPTY;
      }),
      finalize(() => this.loading.update(s => ({ ...s, ehrList: false })))
    );
  }

  downloadEhrDocument(ehr: Ehr, filters: EhrListFiltersValue): Observable<void> {
    const user = this.authService.currentUser();
    if (!user) {
      this.toastr.error('Felhasználó nem található');
      return EMPTY;
    }

    this.loading.update(s => ({ ...s, ehrDocument: true }));

    const request: GetEhrDocumentRequest = {
      breakGlass: filters.breakGlass ? 'true' : 'false',
      breakGlassOk: filters.breakGlassOk ?? '',
      callerTransactionId: '',
      eesztId: ehr.eesztId,
      servicePointId: user.servicePoint?.id ?? 0,
      userId: user.id
    };

    return this.http.post<EhrDocument>(
      `${this.API_URL}/rest/eeszt/ehr/get-ehr`,
      request
    ).pipe(
      tap(doc => this.triggerDownload(doc)),
      map(() => void 0),
      catchError(error => {
        console.error('EHR document download error:', error);
        this.toastr.error('Hiba történt a dokumentum letöltésekor');
        return EMPTY;
      }),
      finalize(() => this.loading.update(s => ({ ...s, ehrDocument: false })))
    );
  }

  loadBcpMessages(): Observable<void> {
    this.loading.update(s => ({ ...s, bcpMessages: true }));
    this.clearBcpSelection();

    return this.http.get<BcpMessage[]>(
      `${this.API_URL}/rest/bcp-message/find-all`
    ).pipe(
      tap(messages => {
        this.bcpMessages.set(messages);
        if (messages.length === 0) {
          this.toastr.info('Nincs megjeleníthető üzenet');
        }
      }),
      map(() => void 0),
      catchError(error => {
        console.error('BCP messages error:', error);
        this.toastr.error('Hiba történt az üzenetek lekérdezésekor');
        return EMPTY;
      }),
      finalize(() => this.loading.update(s => ({ ...s, bcpMessages: false })))
    );
  }

  loadBcpCases(filters: BcpCaseFilters): Observable<void> {
    this.loading.update(s => ({ ...s, bcpMessages: true }));

    let params = new HttpParams()
      .set('onlyError', String(filters.onlyError))
      .set('page', String(filters.page))
      .set('size', String(filters.size));

    if (filters.fromDate) {
      params = params.set('fromDate', filters.fromDate);
    }
    if (filters.toDate) {
      params = params.set('toDate', filters.toDate);
    }
    if (filters.filter && filters.filter.length >= 3) {
      params = params.set('filter', filters.filter);
    }

    const url = `${this.API_URL}/rest/bcp-message/page/find-all`;

    return this.http.get<BcpCasePageResponse>(url, { params }).pipe(
      tap(response => {
        const casesWithState = response.content.map(c => ({ ...c, expanded: false }));
        this.bcpCases.set(casesWithState);
        this.bcpPagination.set(response.page);
        if (response.content.length === 0) {
          this.toastr.info('Nincs megjeleníthető üzenet');
        }
      }),
      map(() => void 0),
      catchError(error => {
        console.error('BCP cases error:', error);
        this.toastr.error('Hiba történt az üzenetek lekérdezésekor');
        return EMPTY;
      }),
      finalize(() => this.loading.update(s => ({ ...s, bcpMessages: false })))
    );
  }

  toggleCaseExpansion(logNumber: string, logPrefix: string): void {
    this.bcpCases.update(cases =>
      cases.map(c =>
        c.logNumber === logNumber && c.logPrefix === logPrefix
          ? { ...c, expanded: !c.expanded }
          : c
      )
    );
  }

  resendBcpMessage(messageId: number): Observable<boolean> {
    this.loading.update(s => ({ ...s, resending: true }));

    return this.http.post<void>(
      `${this.API_URL}/rest/bcp-message/resend-all`,
      [messageId]
    ).pipe(
      tap(() => this.toastr.success('Üzenet újraküldve')),
      map(() => true),
      catchError(error => {
        console.error('BCP resend error:', error);
        this.toastr.error('Hiba történt az üzenet újraküldésekor');
        return EMPTY;
      }),
      finalize(() => this.loading.update(s => ({ ...s, resending: false })))
    );
  }

  resendBcpMessages(ids: number[]): Observable<void> {
    if (ids.length === 0) {
      this.toastr.warning('Nincs kiválasztott üzenet');
      return EMPTY;
    }

    this.loading.update(s => ({ ...s, resending: true }));

    return this.http.post<void>(
      `${this.API_URL}/rest/bcp-message/resend-all`,
      ids
    ).pipe(
      tap(() => {
        this.toastr.success('Üzenetek újraküldve');
        this.clearBcpSelection();
      }),
      switchMap(() => this.loadBcpMessages()),
      catchError(error => {
        console.error('BCP resend error:', error);
        this.toastr.error('Hiba történt az üzenetek újraküldésekor');
        return EMPTY;
      }),
      finalize(() => this.loading.update(s => ({ ...s, resending: false })))
    );
  }

  resendAllBcpMessages(): Observable<void> {
    const user = this.authService.currentUser();
    if (!user) {
      this.toastr.error('Felhasználó nem található');
      return EMPTY;
    }

    this.loading.update(s => ({ ...s, resending: true }));

    return this.http.get<void>(
      // QUESTION: use eesztUserId instead of username ???
      `${this.API_URL}/rest/bcp-message/resend-all/${user.username}`
    ).pipe(
      tap(() => {
        this.toastr.success('Összes üzenet újraküldve');
        this.clearBcpSelection();
      }),
      switchMap(() => this.loadBcpMessages()),
      catchError(error => {
        console.error('BCP resend all error:', error);
        this.toastr.error('Hiba történt az üzenetek újraküldésekor');
        return EMPTY;
      }),
      finalize(() => this.loading.update(s => ({ ...s, resending: false })))
    );
  }

  getBcpMessageCount(): Observable<number> {
    const user = this.authService.currentUser();
    if (!user) {
      return EMPTY;
    }

    return this.http.get<{ numberOfMessages: number }>(
      // QUESTION: use eesztUserId instead of username ???
      `${this.API_URL}/rest/bcp-message/get-size/${user.username}`
    ).pipe(
      map(response => response.numberOfMessages),
      catchError(() => EMPTY)
    );
  }

  toggleBcpSelection(id: number): void {
    this.selectedBcpIds.update(set => {
      const newSet = new Set(set);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }

  clearBcpSelection(): void {
    this.selectedBcpIds.set(new Set());
  }

  searchEbeu(request: EbeuSearchRequest): Observable<void> {
    this.loading.update(s => ({ ...s, ebeuSearch: true }));
    this.ebeuResults.set([]);

    return this.http.post<EbeuSearchResponse>(`${this.API_URL}/rest/eeszt/ebeu/search`, request).pipe(
      tap(response => {
        this.ebeuResults.set(response.data ?? []);
        if (!response.data?.length) {
          this.toastr.info('Nincs találat');
        }
      }),
      map(() => void 0),
      catchError(error => {
        this.toastr.error('Hiba történt az e-beutalók lekérdezése során.');
        return EMPTY;
      }),
      finalize(() => this.loading.update(s => ({ ...s, ebeuSearch: false })))
    );
  }

  resetState(): void {
    this.ehrList.set([]);
    this.ehrWarnings.set([]);
    this.ebeuResults.set([]);
    this.bcpMessages.set([]);
    this.clearBcpSelection();
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private triggerDownload(doc: EhrDocument): void {
    const link = document.createElement('a');
    link.href = `data:application/pdf;base64,${doc.document}`;
    link.download = `ehr_${doc.documentID || 'document'}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}

